<script setup lang="ts">
import { EmissionBudgetState } from "@/api/composables/useEmissionBudgets.composable";
import { DashboardTransportInstallationBudgetType } from "@/features/Dashboard";
import { formatNumber } from "@/services/Utils/DecimalFormatter";
import ProjectCardProgressBar from "./ProjectCardProgressBar.vue";

/**
 * The results block of a project card: the emission bar, the BR18 2025 A4-A5 bar beside it, and the
 * caption row under both.
 *
 * One component because the card renders this twice — once in the navigable `hasAccess` branch and
 * once in the locked branch the request-access modal previews. The two copies were verbatim
 * duplicates that had already drifted apart on `show-loading-spinner`, and a row that only one of
 * them reserved height for is exactly how the cards in a grid row stopped lining up (RTLCA-5561).
 */
defineProps<{
  emissionBudget?: EmissionBudgetState;
  emissionBudgetLabel: string;
  emissionBudgetSubLabel: string;
  emissionUnitLabel: string;
  transportInstallationBudget: DashboardTransportInstallationBudgetType | null;
  transportInstallationPercentage: number;
  transportInstallationSubLabel: string;
  /** Share of the budget used, already resolved through `getUsedEmission` by the card. */
  usedPercentage: number;
}>();

const emit = defineEmits<{
  (e: "refetch"): void;
}>();
</script>

<template>
  <div class="my-4 flex flex-wrap gap-2 px-4">
    <div class="min-w-0 flex-1 basis-36">
      <ProjectCardProgressBar
        :label="() => emissionBudgetLabel"
        :error="emissionBudget?.error"
        :loading="!emissionBudget?.data || false"
        :force-open="true"
        :show-loading-spinner="emissionBudget?.loading"
        :sub-label="() => emissionBudgetSubLabel"
        :disable-auto-close="true"
        :thresholds="[
          { value: 0, color: 'bg-slate-300' },
          { value: 30, color: 'bg-app-green-400' },
          { value: 60, color: 'bg-app-yellow-400' },
          { value: 90, color: 'bg-app-red-300' },
        ]"
        :value="usedPercentage"
        @refetch="emit('refetch')" />
    </div>
    <div
      v-if="transportInstallationBudget"
      class="min-w-0 flex-1 basis-36">
      <ProjectCardProgressBar
        :label="() => 'A4-A5'"
        :sub-label="() => transportInstallationSubLabel"
        :force-open="true"
        :disable-auto-close="true"
        :thresholds="[
          { value: 0, color: 'bg-slate-300' },
          { value: 30, color: 'bg-app-green-400' },
          { value: 60, color: 'bg-app-yellow-400' },
          { value: 90, color: 'bg-app-red-300' },
        ]"
        :value="transportInstallationPercentage" />
    </div>
    <!-- One caption row for the bars above it: the unit the figures are measured in, and how
         complete the mapping behind them is. Both belong to the numbers — the unit floated
         under the status chip first (RTLCA-5419) and read as an orphan there, and at 57 %
         mapped the emission figure is a partial number.

         Rendered unconditionally, with a floor under it: a card whose emissions errored or never
         arrived has neither a unit nor a mapping share, and an empty row collapses to height 0 —
         which made that card shorter than its neighbours (RTLCA-5561). `t-footnote` measures 13px
         (10px at leading-normal 1.3), so `min-h-px-16` — the nearest spacing token above it — puts
         the filled and the empty row on the same 16px. -->
    <div class="t-footnote text-type-muted min-h-px-16 flex basis-full items-center justify-between gap-2">
      <span class="truncate">{{ emissionUnitLabel }}</span>
      <span
        v-if="emissionBudget?.data?.mapping"
        v-tooltip="{ text: 'Share of building elements mapped', variant: 'label' }"
        class="shrink-0">
        {{ formatNumber(emissionBudget.data.mapping.material.percentage) }} % mapped
      </span>
    </div>
  </div>
</template>
